import React, {useEffect, useContext} from 'react';
import * as Yup from 'yup';
import axios from 'axios';
import {
    Alert,
    Box,
    Close,
    Heading,
    Text,
} from 'theme-ui';
import { Submit } from '~/Forms/Submit';
import { Field } from '~/Forms/Field';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { InstitutionalOnboardingContext } from '../InstitutionalLanding';
import { getOnboardingStatusAsync, institutionalOnboardingInstitution } from 'features/auth/authSlice';
import {
    utilStateByCountry,
    utilTimezoneByCountry,
} from 'features/theme/themeSlice';
import { toast } from 'utils/toast';
import { connect, useDispatch } from 'react-redux';
import AddressFields from '~/Forms/AddressFields/AddressFields';

const mapStateToProps = (state) => {
    return { user: state.user }
}

const validationSchema = Yup.object().shape({
    name: Yup.string().required(),
    designation: Yup.string().nullable(),
    admin_email: Yup.string().email().required('required field'),
    admin_phone: Yup.string().required('required field'),
    afsl: Yup.string().required('required field'),
    car: Yup.string().nullable(),
    pid: Yup.string().required('required field'),
    unit_number: Yup.string().nullable(),
    street_number: Yup.string().nullable(),
    street_name: Yup.string().nullable(),
    street_type: Yup.string().nullable(),
    address2: Yup.string().nullable(),
    state: Yup.string().nullable(),
    suburb: Yup.string().nullable(),
    postcode: Yup.string().nullable(),
    country: Yup.string().nullable(),
});

const InstitutionalOnboardingInstitution = ({ user }) => {
    const onboarding = useContext(InstitutionalOnboardingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    useEffect(() => {
        axios.all([
            utilStateByCountry('AU'),
            utilTimezoneByCountry('AU')
        ]).then(axios.spread((stateResponse, timezoneResponse) => {
            onboarding.setLoading(false);
        })).catch(({ response }) => {
            onboarding.setLoading(false);
        });
    }, [onboarding]);

    const handleSubmit = (values, actions) => {
        onboarding.setLoading(true);

        institutionalOnboardingInstitution({
            admin_email: values.admin_email,
            admin_phone: values.admin_phone,
            afsl: values.afsl,
            car: values.car,
            pid: values.pid,
            address: values.address,
            unit_number: values.unit_number,
            street_number: values.street_number,
            street_name: values.street_name,
            street_type: values.street_type,
            address2: values.address2,
            state: values.state,
            suburb: values.suburb,
            postcode: values.postcode,
            country: values.country ?? 'AU',
            name: values.name,
            designation: values.designation,
        }).then((response) => {
            toast.success(t('onboarding.institutional.landing.institution.success'), {
                toastId: 'entity-success',
            });
            onboarding.setLoading(false);
            onboarding.nextStep();
            dispatch(getOnboardingStatusAsync());
            onboarding.setLoading(false);
        }).catch(({ response }) => {
            response?.data?.errors && actions.setErrors(response.data.errors);
            toast.error(response?.data?.message || t('onboarding.institutional.landing.institution.error'), {
                toastId: 'entity-error',
            });
            actions.setStatus('api_error');
            actions.setSubmitting(false);
            onboarding.setLoading(false);
        });
    };

    return (
        <>
            <Formik
                enableReinitialize
                initialValues={{
                    admin_email: user?.user?.institution?.admin_email ?? '',
                    admin_phone: user?.user?.institution?.admin_phone ?? '',
                    afsl: user?.user?.institution?.afsl ?? '',
                    car: user?.user?.institution?.car ?? '',
                    pid: user?.user?.institution?.pid ?? '',
                    unit_number: user?.user?.institution?.address?.unit_number ?? '',
                    street_number: user?.user?.institution?.address?.street_number ?? '',
                    street_name: user?.user?.institution?.address?.street_name ?? '',
                    street_type: user?.user?.institution?.address?.street_type ?? '',
                    address2: user?.user?.institution?.address?.address2 ?? '',
                    suburb: user?.user?.institution?.address?.suburb ?? '',
                    state: user?.user?.institution?.address?.state ?? '',
                    postcode: user?.user?.institution?.address?.postcode ?? '',
                    country: user?.user?.institution?.address?.country ?? 'AU',
                    name:  user?.user?.institution?.name ?? '',
                    designation: user?.user?.institution?.designation ?? '',
                }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ status, setStatus, setFieldValue, values }) => (
                    <Box id="onboardingEntity">
                        { status === 'api_error' ?
                            <Alert role="alert" variant='danger' mb={2}>
                                {t('onboarding.institutional.landing.institution.error')}
                                <Close ml="auto" mr={-2} onClick={() => setStatus(null)} />
                            </Alert> : ''}

                        <Form>
                            <Heading as="h1" variant="h3" sx={{ fontWeight: 500 }} mb={4}>
                                {t('onboarding.institutional.landing.institution.title')}
                            </Heading>

                            <Heading as="h2" variant="form_section" mb={3} sx={{
                                fontWeight: 700,
                                fontSize: 2
                            }}>
                                {t('onboarding.institutional.landing.institution.subtitle')}
                            </Heading>


                            <Field
                                label="Institution Name"
                                name="name"
                                required={true}
                            />

                            <Box sx={{ position: 'relative' }}>
                                <Field
                                    label="Designation"
                                    name="designation"
                                    description="eg. Investment Fund A/C"
                                />
                                <Box sx={{
                                    position: 'absolute',
                                    height: '70px',
                                    top: 0,
                                    right: 3,
                                    pointerEvents: 'none',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                }}>
                                    <Text>A/C</Text>
                                </Box>
                            </Box>

                            <Field
                                label="Admin Email"
                                name="admin_email"
                                required={true}
                            />

                            <Field
                                label="Admin Phone Number"
                                name="admin_phone"
                                field="phone"
                                required={true}
                            />

                            <Field
                                label="AFSL"
                                name="afsl"
                                required={true}
                            />

                            <Field
                                label="CAR"
                                name="car"
                            />

                            <Field
                                label="PID"
                                name="pid"
                                required={true}
                            />

                            <Heading
                                as="h2"
                                variant="form_section"
                                mb={3}
                                mt={4}
                                sx={{
                                    fontWeight: 700,
                                    fontSize: 2,
                                }}
                            >
                                {t('Institution Address')}
                            </Heading>

                            <AddressFields setFieldValue={setFieldValue} context={onboarding} subtitle=' ' />

                            <Submit variant="primary" id="institutionSubmit" text={t('onboarding.institutional.landing.institution.submit')} sx={{ display: 'block', width: '100%' }} />
                        </Form>
                    </Box>
                )}
            </Formik>
        </>
    )
};

export default connect(mapStateToProps, null)(InstitutionalOnboardingInstitution);
